import classnames from 'classnames';

import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';

import { CSWFieldId, getOrderDetailsFieldId } from '../../../../../constants/field-ids';
import PackageDropdownContainer from '../../../../../fulfillment-planner/package/dropdown/container';
import MultiPackage from '../../../../../fulfillment-planner/package/multi-package';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import { MultiPackageButton } from '../../../../../fulfillment-planner/package/multi-package/multi-package-button';
import { PackageLabel } from '../../../shared-field-labels/shared-field-labels';
import LabeledField from '../labeled-field';
import { LabeledFieldWithButton } from '../labeled-field-with-button';
import { useEnableMultiPackageShipmentsOrderDetails } from '../../../../../feature-flags/hooks/use-multi-package-shipping-order-details';
import { Segment } from '../../../../../services/analytics-services/segment';
import {
  MULTI_PACKAGE_DROPDOWN_SELECT_FROM_ORDER_DETAILS,
  MULTI_PACKAGE_MODAL_OPEN_FROM_ORDER_DETAILS,
} from '../../../../../constants/analytics-events';
import { usePackingApplied } from '../packing-recommend/use-packing-applied';
import { FieldLabelWithIndicators } from '../packing-recommend/components/field-label-with-indicators/field-label-with-indicators';

import style from './package.module.less';

const fieldId = getOrderDetailsFieldId(CSWFieldId.Package);

/**
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {string?} [props.containerClassName]
 * @param {boolean?} [props.hideMultiPackage]
 * @param {boolean?} [props.isDisabled]
 * @returns {JSX.Element}
 */
const Package = ({ fulfillmentPlanId, containerClassName = '', hideMultiPackage = false, isDisabled = false }) => {
  const enableMultiPackageShipmentsOrderDetails = useEnableMultiPackageShipmentsOrderDetails();
  const { hasSmartFillDataBeenChanged, isSmartFillEnabled, smartFillMetaData } = useFulfillmentPlannerContext();
  const { isPackingApplied } = usePackingApplied(fulfillmentPlanId);

  const hasSmartFillPackageChanged = hasSmartFillDataBeenChanged?.package;
  const shouldShowSmartFillIcon = isSmartFillEnabled && !hasSmartFillPackageChanged;

  const onTrackChangePackageSet = (packageSet) => {
    Segment.trackEvent(MULTI_PACKAGE_DROPDOWN_SELECT_FROM_ORDER_DETAILS, {
      type: MULTI_PACKAGE_DROPDOWN_SELECT_FROM_ORDER_DETAILS,
      packageSetName: packageSet?.name,
    });
  };

  const onTrackOpenMultiPackageModal = () => {
    Segment.trackEvent(MULTI_PACKAGE_MODAL_OPEN_FROM_ORDER_DETAILS, {
      type: MULTI_PACKAGE_MODAL_OPEN_FROM_ORDER_DETAILS,
    });
  };

  const renderLabel = () => (
    <FieldLabelWithIndicators
      isPackingApplied={isPackingApplied}
      shouldShowSmartFillIcon={shouldShowSmartFillIcon}
      smartFillCount={smartFillMetaData?.Package?.TimesUsed}
    >
      {PackageLabel}
    </FieldLabelWithIndicators>
  );

  if (enableMultiPackageShipmentsOrderDetails) {
    return (
      <LabeledFieldWithButton
        button={
          <div className={style['package-buttons-container']}>
            <MultiPackageButton
              buttonClassName={style['multi-package-button']}
              fulfillmentPlanId={fulfillmentPlanId}
              iconClassName={style['multi-package-button-icon']}
              onTrackOpenMultiPackageModal={onTrackOpenMultiPackageModal}
            />
          </div>
        }
        className={classnames({ [style['packing-applied-field']]: isPackingApplied })}
        label={renderLabel()}
        labelFor={fieldId}
      >
        <div className={classnames(containerClassName, { [style['packing-applied-dropdown']]: isPackingApplied })}>
          <PackageDropdownContainer
            dropdownMenuType={DropdownMenuType.ComboBox}
            id={fieldId}
            isDisabled={isDisabled}
            onTrackChangePackageSet={onTrackChangePackageSet}
          />
        </div>
      </LabeledFieldWithButton>
    );
  }

  return (
    <LabeledField
      button={
        !hideMultiPackage && (
          <div className={style['package-buttons-container']}>
            <MultiPackage
              buttonClassName={style['package-editor-button']}
              fulfillmentPlanId={fulfillmentPlanId}
              iconClassName={style['package-editor-button-icon']}
              onTrackOpenMultiPackageModal={onTrackOpenMultiPackageModal}
            />
          </div>
        )
      }
      className={classnames({ [style['packing-applied-field']]: isPackingApplied })}
      label={renderLabel()}
      labelFor={fieldId}
    >
      <div className={classnames(containerClassName, { [style['packing-applied-dropdown']]: isPackingApplied })}>
        <PackageDropdownContainer
          dropdownMenuType={DropdownMenuType.ComboBox}
          id={fieldId}
          isDisabled={isDisabled}
          onTrackChangePackageSet={onTrackChangePackageSet}
        />
      </div>
    </LabeledField>
  );
};

export default Package;
